import { Connect, ConnectConfig, defaultConnectConfig } from '@genesislcap/foundation-comms';
import { Navigation } from '@genesislcap/foundation-header';
import { baseLayerLuminance, StandardLuminance } from '@microsoft/fast-components';
import { FASTElement, customElement, observable } from '@microsoft/fast-element';
import { Container, inject, Registration } from '@microsoft/fast-foundation';
import { DefaultRouteRecognizer } from '@microsoft/fast-router';
import { DynamicTemplate as template, LoadingTemplate, MainTemplate } from './main.template';
import { MainStyles as styles } from './main.styles';
import { MainRouterConfig } from '../routes';
import * as Components from '../components';
import { logger } from '../utils';
import Highcharts from 'highcharts';

const name = 'cascade-app';

// eslint-disable-next-line
declare var API_HOST: string;

export const hostEnv = location.host;
const hostUrl = API_HOST || `wss://${hostEnv}/gwf/`;

@customElement({
  name,
  template,
  styles,
})
export class MainApplication extends FASTElement {
  @inject(MainRouterConfig) config!: MainRouterConfig;
  @inject(Navigation) navigation!: Navigation;
  @Connect connect!: Connect;
  @Container container!: Container;
  @observable provider!: any;
  @observable ready: boolean = false;
  @observable data: any = null;

  async connectedCallback() {
    super.connectedCallback();


    logger.debug(`${name} is now connected to the DOM`);
    this.checkForSSOToken();
    this.registerDIDependencies();
    await this.loadRemotes();
    this.loadHighchartsTheme();
  }

  checkForSSOToken(){
    const queryParams = new  URLSearchParams(window.location.search);
    const ssoToken = queryParams.get('SSO_TOKEN');
    if(ssoToken) {
      if (window.opener){
        window.opener.sessionStorage.setItem('ssoToken', ssoToken);
        window.opener.location.reload();
        window.close();
      } else {
        sessionStorage.setItem('ssoToken', ssoToken);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  onDarkModeToggle() {
    baseLayerLuminance.setValueFor(
      this.provider,
      baseLayerLuminance.getValueFor(this.provider) === StandardLuminance.DarkMode
        ? StandardLuminance.LightMode
        : StandardLuminance.DarkMode
    );
  }

  async loadRemotes() {
    /**
     * TODO: Send event to indicate some async work is happening.
     * Will be picked up by overlay micro frontend.
     */
    await Components.loadRemotes();
    /**
     * Simulate loading delay
     * await new Promise(resolve => setTimeout(resolve, 3000));
     */
    this.ready = true;
  }

  /**
   * You can use various directives in templates like when(), which enables conditional rendering,
   * and you can also split your templates up and return them based on some state.
   */
  public selectTemplate() {
    return this.ready ? MainTemplate : LoadingTemplate;
  }

  public handleDoingSomething(detail: any) {
    logger.debug(`handleDoingSomething in main ${detail}`);
  }

  private registerDIDependencies() {
    this.container.register(
      Registration.transient(DefaultRouteRecognizer, DefaultRouteRecognizer),
      /**
       * Register custom configs for core services and micro frontends. Note this can also be done via
       * provideDesignSystem().register(...) if preferred.
       *
       * Registration.instance<CredentialManagerConfig>(CredentialManagerConfig, {
       *  ...defaultCredentialManagerConfig,
       *  cookie: {
       *    ...defaultCredentialManagerConfig.cookie,
       *    path: '/login',
       *  },
       * }),
       */
      Registration.instance<ConnectConfig>(ConnectConfig, {
        ...defaultConnectConfig,
        connect: {
          ...defaultConnectConfig.connect,
          heartbeatInterval: 15_000,
        },
      })
    );
  }

  private loadHighchartsTheme() {
    Highcharts.theme = {
      colors: [
        '#1ABC9C',
        '#8E44AD',
        '#C0392B',
        '#2980B9',
        '#F1C40F',
        '#27AE60',
        '#95A5A6',
        '#D35400',
        '#3498DB',
        '#BDC3C7',
        '#F39C12',
        '#16A085',
        '#ECF0F1',
        '#E74C3C',
        '#7F8C8D',
        '#9B59B6',
        '#2ECC71',
        '#34495E',
        '#E67E22',
      ],
      chart: {
        backgroundColor: '#202126',
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      xAxis: {
        gridLineColor: '#3D444A',
        labels: {
          style: {
            color: '#879BA6',
            fontWeight: 'normal',
            fontSize: '12px',
          },
        },

        lineColor: '#879BA6',
        lineWidth: 3,
        tickColor: '#707073',
        title: {
          style: {
            color: '#A0A0A3',
            margin: 80,
          },
        },
      },
      yAxis: {
        gridLineWidth: 1,
        gridLineColor: '#2E3339',
        labels: {
          style: {
            color: '#5E6B74',
          },
        },
        lineColor: '#707073',
        tickWidth: 0,
        title: {
          style: {
            color: '#879ba6',
            fontWeight: 'bold',
          },
        },
      },
      tooltip: {
        backgroundColor: '#000000',
        borderColor: '#2E3339',
        shared: true,
        style: {
          color: '#F1F1F1',
          cursor: 'default',
          fontSize: '12px',
          whiteSpace: 'nowrap',
        },
      },
      legend: {
        itemStyle: {
          color: '#879ba6',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: 'bold',
          textOverflow: 'ellipsis',
        },
        itemHoverStyle: {
          color: '#fff',
        },
      },
      lang: {
        decimalPoint: '.',
        thousandsSep: ',',
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
          lineWidth: 2,
        },
        column: {
          pointWidth: 10,
          borderWidth: 0,
        },
      },
    };
    // Apply the theme
    Highcharts.setOptions(Highcharts.theme);
  }
}
